import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CalendarClock, Plus } from "lucide-react";
import { toast } from "sonner";

const TYPES = { speech: "تخاطب", occupational: "علاج وظيفي", behavioral: "علاج سلوكي", other: "أخرى" };
const empty = { child_id: "", therapist_id: "", session_type: "speech", start_at: "", end_at: "", notes: "" };

export default function SessionsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [children, setChildren] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data: s } = await api.get("/sessions");
    setSessions(s);
    if (user.role === "admin") {
      const [{ data: c }, { data: t }] = await Promise.all([
        api.get("/children"),
        api.get("/users?role=teacher"),
      ]);
      setChildren(c);
      setTherapists(t);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/sessions", form);
      toast.success("تم جدولة الجلسة وإرسال تذكير للأهل (محاكاة)");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="sessions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">جدولة الجلسات</h1>
          <p className="text-slate-500 mt-2">تخاطب، وظيفي، سلوكي — مع تذكيرات واتساب تلقائية</p>
        </div>
        {user.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-session-btn">
                <Plus className="w-4 h-4" /> جدولة جلسة
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-2xl font-black">جلسة جديدة</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label className="font-bold">الطفل</Label>
                  <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                    <SelectTrigger data-testid="session-child"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">المعالج</Label>
                  <Select value={form.therapist_id} onValueChange={(v) => setForm({ ...form, therapist_id: v })}>
                    <SelectTrigger data-testid="session-therapist"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{therapists.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">نوع الجلسة</Label>
                  <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
                    <SelectTrigger data-testid="session-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-bold">البداية</Label>
                    <Input data-testid="session-start" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-bold">النهاية</Label>
                    <Input data-testid="session-end" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
                  </div>
                </div>
                <Button data-testid="save-session-btn" onClick={save} className="w-full h-12 bg-sky-600 hover:bg-sky-700">حفظ</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sessions.map((s) => (
          <Card key={s.id} data-testid={`session-${s.id}`} className="border-slate-200/70 card-elevated">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                <CalendarClock className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase text-sky-700">{TYPES[s.session_type] || s.session_type}</span>
                </div>
                <div className="font-black text-slate-900 mt-1">{s.child_name}</div>
                <div className="text-sm text-slate-600">مع {s.therapist_name}</div>
                <div className="text-xs text-slate-500 mt-2">
                  {new Date(s.start_at).toLocaleString("ar-EG")}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {sessions.length === 0 && <Card className="md:col-span-2"><CardContent className="p-16 text-center text-slate-400">لا توجد جلسات مجدولة</CardContent></Card>}
      </div>
    </div>
  );
}
